import { useCallback, useMemo } from 'react';
import { HONEYMOON_TAG, HotelSearchMapLayout } from '@shared/constants/accommodations';
import { createSearchParamsForGuestMap, useHotelRoutePaths } from '@shared/utils/accommodations/hotelRoutes';
import { useTravelCashFundBalance } from '@shared/hooks/useTravelCashFundBalance/useTravelCashFundBalance';
import { useTravelCurrencyAllowlist } from '@shared/hooks/useTravelCurrencyAllowlist';
import { toHoneymoonSearchLocationParams, useTravelHoneymoonConfig } from '@shared/hooks/useTravelHoneymoonConfig/useTravelHoneymoonConfig';
import type { TravelHoneymoonConfig } from '@shared/hooks/useTravelHoneymoonConfig/useTravelHoneymoonConfig.types';
import type { TravelCashFundBestCoupon } from '@shared/hooks/useTravelCashFundBalance/useTravelCashFundBalance.types';
import { calculateStayDates } from '@shared/utils/dateHelpers';
import { HONEYMOON_SEARCH_PARAM, HONEYMOON_SEARCH_PARAM_VALUE } from '@shared/constants/honeymoonFundSearch';
import type { HoneymoonTripSelection, HoneymoonUnlockUrlOverrides, HoneymoonUtmOverrides } from './useHoneymoonSearchUrl.types';

type UseHoneymoonSearchUrlArgs = Readonly<{
  eventHandle: string;
  eventId: string | undefined;
  /** Caller's pre-balance gate: eligible cohort + cash-fund flag on. Nothing loads until true. */
  isCashFundSurfaceEnabled: boolean;
  /** Per-entry-point UTM slots; omitted slots keep the guest-map defaults. */
  utmOverrides?: HoneymoonUtmOverrides;
}>;

type HoneymoonSearchUrlResult = Readonly<{
  unlockOffersUrl: string;
  /**
   * The same URL for a different entry point, without a second call to this hook.
   *
   * Two surfaces on one page need two `utm_source` values but must share one flag read: the
   * experiment exposure is enqueued per evaluation and never deduped, so a second call double-counts
   * the page view. Hoist one call and let each surface build its own link from this.
   *
   * Each UTM slot falls back to the one this hook was configured with, so overriding only the trip
   * keeps the caller's attribution rather than reverting to the guest-map default.
   */
  buildUnlockOffersUrl: (overrides?: HoneymoonUnlockUrlOverrides) => string;
  /**
   * The trip `unlockOffersUrl` was built from: the configured destination and the default stay
   * dates. Seed a destination/date picker from this so the fields open on exactly what a couple who
   * changes nothing would get.
   */
  defaultTrip: HoneymoonTripSelection;
  /** False until the fund currency AND the location flag resolve — expose the CTA only when true, or a fast click opens the wrong search. */
  isUrlReady: boolean;
  /**
   * True when the flag configures a destination, so `unlockOffersUrl` opens somewhere real.
   * False means a CTA must collect one first — see the `defaultTrip` note below.
   */
  hasStartingLocation: boolean;
  /**
   * The couple's best cash-fund codes, at most one per payment rule.
   *
   * The whole list, not the headline: `CASH_FUND` prices on the fund amount and
   * `CASH_FUND_AND_CARD` prices on the whole stay, so a caller has to pick the one whose base it can
   * actually quote. Carried out of here rather than read again elsewhere — this hook already owns
   * the balance query, and a second call would be a second round trip for the same answer.
   */
  bestCashFundCoupons: ReadonlyArray<TravelCashFundBestCoupon>;
  /** Spendable travel-fund balance in minor units; 0 when there is no usable fund. */
  fundBalanceMinorUnits: number;
  /** ISO 4217 currency of that balance; undefined when there is no fund. */
  fundCurrency: string | undefined;
  /** The travel credit (wallet) part of that balance; undefined when unknown. */
  travelCreditMinorUnits: number | undefined;
  /** The cash fund (Stripe) part of that balance; undefined when unknown. */
  cashFundMinorUnits: number | undefined;
  /**
   * Whether the hotel search will actually price in the fund's currency.
   *
   * The link stamps `currency=<fundCurrency>`, and the search page drops that param when the code
   * is outside the `travelCurrencies` allowlist — falling back to USD, where the fund can never be
   * applied. The couple then gets a notice telling them to switch to a currency the picker does not
   * offer. So a surface that quotes the balance must gate on this, not just on the currency existing.
   */
  isFundCurrencySupported: boolean;
  /** The whole flag payload, so a caller needing another setting reuses this read instead of adding one. */
  honeymoonConfig: TravelHoneymoonConfig;
}>;

/**
 * Single source for the honeymoon "Unlock Offers" hotel-search link: default honeymoon
 * location (flag payload or fallback) plus the fund currency, so every cash-fund entry
 * point produces the same URL shape.
 *
 * The link carries `prioritizeTag=honeymoon`, which is what makes this the honeymoon search
 * rather than a geographic one that happens to open on a honeymoon destination. Without it the
 * couple lands on the same undifferentiated result list any guest would get: the curated stays
 * are in the response, just not lifted, and nothing on screen says the CTA did anything.
 */
export const useHoneymoonSearchUrl = ({ eventHandle, eventId, isCashFundSurfaceEnabled, utmOverrides }: UseHoneymoonSearchUrlArgs): HoneymoonSearchUrlResult => {
  const { config: honeymoonConfig, isLoading: isHoneymoonLocationLoading } = useTravelHoneymoonConfig({ skip: !isCashFundSurfaceEnabled });
  // Also the balance every caller gates on: this query already runs here for the URL currency, and it
  // is `network-only`, so a second call site would cost a second round trip for the same answer.
  const {
    totalSpendableMinorUnits,
    travelCreditMinorUnits,
    cashFundMinorUnits,
    fundCurrency: cashFundCurrency,
    bestCashFundCoupons,
    isLoading: isCashFundBalanceLoading,
  } = useTravelCashFundBalance({ eventId, skip: !isCashFundSurfaceEnabled });
  // Skipped with the same gate as everything else here, so no exposure fires outside the surface.
  const { allowedSet, loading: isCurrencyAllowlistLoading } = useTravelCurrencyAllowlist({ skip: !isCashFundSurfaceEnabled });
  const hotelRoutePaths = useHotelRoutePaths();
  const overriddenUtmSource = utmOverrides?.utm_source;
  const overriddenUtmCampaign = utmOverrides?.utm_campaign;

  /**
   * Where and when the link opens on, before the couple changes anything.
   *
   * `location` is the flag's, and is often unset — there is no fallback destination. A link without
   * it does not open on an empty map: `useDeriveLocationQueryParams` falls back to the wedding
   * venue, then the event city, then New York. Callers gate on `hasStartingLocation` to avoid that.
   *
   * The dates are `calculateStayDates()` with no argument — one month out, a day either side.
   * Deliberately NOT the event date, and deliberately not `travelSettings.checkInDate`:
   *
   * - The event date is what the search page falls back to when a link carries no dates, and it
   *   produces a two-night stay across the wedding day itself. Nobody honeymoons then; putting
   *   explicit dates on the link is what stops that branch being reached at all.
   * - `travelSettings` holds the **wedding-guest** hotel-block range — the dates guests are told to
   *   book for the wedding weekend. It is read by the guest Accommodations widget and the guest
   *   site's search defaults. Seeding a honeymoon from it lands on the wedding weekend, which is
   *   the same bug in a different disguise.
   *
   * So the honeymoon has no stored dates anywhere, and a neutral month-out default is the honest
   * starting point. The fields in the dialog are how a couple says otherwise.
   */
  const defaultTrip = useMemo<HoneymoonTripSelection>(() => {
    const { checkIn, checkOut } = calculateStayDates();
    return { location: honeymoonConfig.location, checkin: checkIn.dateString, checkout: checkOut.dateString };
  }, [honeymoonConfig.location]);

  const buildUnlockOffersUrl = useCallback(
    (overrides?: HoneymoonUnlockUrlOverrides) => {
      const trip = overrides?.trip ?? defaultTrip;
      // Falls back to this hook's own UTM, per slot, so a caller that overrides only the trip keeps
      // its attribution. Without it the link silently reverts to the guest-map default and the
      // entry point disappears from the funnel — see the Manage Registry dialog, which passes a
      // trip and nothing else.
      const utmSource = overrides?.utm_source ?? overriddenUtmSource;
      const utmCampaign = overrides?.utm_campaign ?? overriddenUtmCampaign;
      return hotelRoutePaths.hotelSearch.buildRelativeUrl(eventHandle, {
        // Dates ride the guest-map params rather than being appended loose, because that helper is
        // the one place that knows these are spelled `checkin`/`checkout` here.
        ...createSearchParamsForGuestMap({ layout: HotelSearchMapLayout.FULL, checkin: trip.checkin, checkout: trip.checkout }),
        ...(trip.location && toHoneymoonSearchLocationParams(trip.location)),
        ...(trip.rooms && { rooms: trip.rooms }),
        // Two different jobs, both needed. honeymoonSearch re-anchors the map (drop the wedding
        // venue pin, keep the couple's picks out of the viewport, zoom to the honeymoon place);
        // prioritizeTag is what makes the *results* lead with curated honeymoon stays. Either one
        // alone leaves half of what the link promises unkept.
        [HONEYMOON_SEARCH_PARAM]: HONEYMOON_SEARCH_PARAM_VALUE,
        prioritizeTag: overrides?.prioritizeTags ?? HONEYMOON_TAG,
        ...(cashFundCurrency && { currency: cashFundCurrency }),
        ...(utmSource && { utm_source: utmSource }),
        ...(utmCampaign && { utm_campaign: utmCampaign }),
      });
    },
    [hotelRoutePaths, eventHandle, defaultTrip, cashFundCurrency, overriddenUtmSource, overriddenUtmCampaign],
  );

  // Built from `buildUnlockOffersUrl` rather than repeating the param list: the two used to be
  // separate copies, and dates would have had to be added to both. The hook's own UTM is applied
  // inside the builder, so this needs no arguments at all.
  const unlockOffersUrl = useMemo(() => buildUnlockOffersUrl(), [buildUnlockOffersUrl]);

  return {
    unlockOffersUrl,
    buildUnlockOffersUrl,
    defaultTrip,
    isUrlReady: isCashFundSurfaceEnabled && !isCashFundBalanceLoading && !isHoneymoonLocationLoading && !isCurrencyAllowlistLoading,
    hasStartingLocation: !!honeymoonConfig.location,
    isFundCurrencySupported: !!cashFundCurrency && allowedSet.has(cashFundCurrency),
    bestCashFundCoupons,
    fundBalanceMinorUnits: totalSpendableMinorUnits ?? 0,
    fundCurrency: cashFundCurrency,
    travelCreditMinorUnits,
    cashFundMinorUnits,
    honeymoonConfig,
  };
};
